<template>
  <cx-toast-card :visible="visible" @close="$emit('close')">
    <div class="csat-popup">
      <div :id="questionId" class="csat-popup__question">{{ title }}</div>
      <star-rating
        v-model="score"
        class="csat-popup__rating"
        :labelled-by="questionId"
        :worst-label="CSAT_RATING_WORST_LABEL"
        :best-label="CSAT_RATING_BEST_LABEL"
      />
      <textarea
        v-model="comment"
        class="csat-popup__comment"
        rows="1"
        :placeholder="CSAT_POPUP_COMMENT_PLACEHOLDER"
        :aria-label="CSAT_POPUP_COMMENT_PLACEHOLDER"
      />
      <div class="csat-popup__error" :role="error ? 'alert' : null">
        <template v-if="error">{{ CSAT_POPUP_ERROR_MESSAGE }}</template>
      </div>
      <riseup-button
        class="csat-popup__submit"
        :title="CSAT_SUBMIT_LABEL"
        :disabled="!score || loading"
        :loading="loading"
        @click="handleSubmit"
      />
    </div>
  </cx-toast-card>
</template>

<script>
import BaseUI from '@riseupil/base-ui';
import {
  CSAT_POPUP_TITLE,
  CSAT_POPUP_COMMENT_PLACEHOLDER,
  CSAT_POPUP_ERROR_MESSAGE,
  CSAT_RATING_BEST_LABEL,
  CSAT_RATING_WORST_LABEL,
  CSAT_SUBMIT_LABEL,
} from '@/constants/chat';
import StarRating from '@/base-components/StarRating.vue';
import CxToastCard from './CxToastCard.vue';

export default {
  name: 'ChatCsatPopup',
  components: {
    CxToastCard,
    StarRating,
    RiseupButton: BaseUI.RiseupButton,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: CSAT_POPUP_TITLE,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      score: 0,
      comment: '',
      questionId: `csat-popup-question-${this._uid}`,
      CSAT_POPUP_COMMENT_PLACEHOLDER,
      CSAT_POPUP_ERROR_MESSAGE,
      CSAT_RATING_BEST_LABEL,
      CSAT_RATING_WORST_LABEL,
      CSAT_SUBMIT_LABEL,
    };
  },
  watch: {
    visible(visible) {
      if (visible) {
        this.score = 0;
        this.comment = '';
      }
    },
  },
  methods: {
    handleSubmit() {
      this.$emit('submit', { score: this.score, comment: this.comment.trim() || null });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@riseupil/base-ui/src/scss/riseup-colors";
@import "~@riseupil/base-ui/src/scss/border-radius";
@import "~@riseupil/base-ui/src/scss/spacings";
@import "~@riseupil/base-ui/src/scss/typography";

// Paddings and widths are the Figma frame's measurements (1406:1926); everything
// with a design token uses the shared variable.
.csat-popup {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 31px 26px;
  direction: rtl;
  font-family: SimplerPro;
  color: $riseup_black;
  text-align: center;
}

.csat-popup__question {
  width: 100%;
  font-size: $ri-font-size-16;
  line-height: 24px;
  margin-bottom: 13px;
}

.csat-popup__rating {
  width: 261px;
  max-width: 100%;
  margin-bottom: 27px;
}

// Not base-ui's TextArea: that is a labelled form field whose focus/filled
// border rules outrank scoped overrides, so the card would fight it.
.csat-popup__comment {
  width: 100%;
  height: 58px;
  box-sizing: border-box;
  padding: 18px 17px;
  border: 1px solid $riseup_gray_20;
  border-radius: $ri-border-radius-medium;
  font-family: inherit;
  font-size: $ri-font-size-14;
  line-height: 20px;
  color: $riseup_black;
  text-align: right;
  resize: none;
  outline: none;
  margin-bottom: $ri-spacing-sm;

  &:focus {
    border-color: $riseup_blue;
  }

  &::placeholder {
    color: rgba($riseup_black, 0.2);
  }
}

.csat-popup__error {
  height: 16px;
  margin-bottom: $ri-spacing-sm;
  font-size: $ri-font-size-12;
  line-height: 16px;
  color: $riseup_danger_red;
}

.csat-popup__submit.riseup-button {
  width: 100%;
  height: 50px;
  border-radius: $button-border-radius;
  font-size: $ri-font-size-16;
}
</style>
